// Canadian provincial tax matrix — mirror of /app/backend/tax.py.
//
// Kept in lockstep with the backend so the in-browser invoice detail
// view's per-row tax math reconciles with what the PDF and the email
// attachment show. If you change one, change the other.
const TAX_TABLE = {
  AB: { gst: 0.05, pst: 0.00, label: "GST" },
  BC: { gst: 0.05, pst: 0.07, label: "GST+PST" },
  MB: { gst: 0.05, pst: 0.07, label: "GST+PST" },
  NB: { gst: 0.15, pst: 0.00, label: "HST" },
  NL: { gst: 0.15, pst: 0.00, label: "HST" },
  NS: { gst: 0.14, pst: 0.00, label: "HST" },
  NT: { gst: 0.05, pst: 0.00, label: "GST" },
  NU: { gst: 0.05, pst: 0.00, label: "GST" },
  ON: { gst: 0.13, pst: 0.00, label: "HST" },
  PE: { gst: 0.15, pst: 0.00, label: "HST" },
  QC: { gst: 0.05, pst: 0.0998, label: "GST+QST" },
  SK: { gst: 0.05, pst: 0.06, label: "GST+PST" },
  YT: { gst: 0.05, pst: 0.00, label: "GST" },
};

const DIESEL_PST_EXEMPT_PROVINCES = new Set(["BC", "MB", "SK"]);
const DIESEL_PST_EXEMPT_PRODUCTS = new Set(["DIESEL_LS", "DSL_EFF_LS"]);

export function taxForProvince(province, product = null) {
  const provKey = (province || "").toUpperCase();
  const prodKey = (product || "").toUpperCase();
  const base = TAX_TABLE[provKey] || { gst: 0.13, pst: 0.0, label: "HST" };
  if (
    DIESEL_PST_EXEMPT_PROVINCES.has(provKey) &&
    DIESEL_PST_EXEMPT_PRODUCTS.has(prodKey) &&
    base.pst > 0
  ) {
    return { gst: base.gst, pst: 0.0, label: "GST" };
  }
  return base;
}

// Given an invoice's line_items, return a rich summary used by the
// in-browser invoice detail view (admin + customer):
//   {
//     rows:    [{ ...line, gst, pst, finalAmount }],
//     totals:  { qty, amount, gst, pst, finalAmount },
//     byProv:  { ON: {gst, pst}, BC: {gst, pst}, ... }  // sorted alpha at render
//   }
// USA carve-out — mirrors backend pricing.compute_final_amount: USA fills carry
// no separately added tax (taxes are embedded in the pump price).
export function isUsaLine(li) {
  return (
    String(li.country || "").toUpperCase() === "US" ||
    String(li.currency || "").toUpperCase() === "USD" ||
    String(li.original_currency || "").toUpperCase() === "USD"
  );
}

export function computeInvoiceTaxBreakdown(lineItems) {
  const rows = [];
  const totals = { qty: 0, amount: 0, gst: 0, pst: 0, finalAmount: 0 };
  const byProv = {};

  for (const li of lineItems) {
    const amt = Number(li.customer_amount || 0);
    const qty = Number(li.qty || 0);
    const prov = (li.province || "").toUpperCase();
    const prod = li.product_canonical || li.product || "";
    const usa = isUsaLine(li);
    const t = usa ? { gst: 0, pst: 0 } : taxForProvince(prov, prod);
    const gst = Math.round(amt * t.gst * 100) / 100;
    const pst = Math.round(amt * t.pst * 100) / 100;
    const finalAmount = Math.round((amt + gst + pst) * 100) / 100;

    rows.push({ ...li, gst, pst, finalAmount });
    totals.qty += qty;
    totals.amount += amt;
    totals.gst += gst;
    totals.pst += pst;
    totals.finalAmount += finalAmount;
    if (prov && !usa) {
      byProv[prov] ||= { gst: 0, pst: 0 };
      byProv[prov].gst += gst;
      byProv[prov].pst += pst;
    }
  }
  // Round final totals to cents to avoid 0.0000001 display noise.
  for (const k of ["qty", "amount", "gst", "pst", "finalAmount"]) {
    totals[k] = Math.round(totals[k] * 100) / 100;
  }
  for (const p of Object.keys(byProv)) {
    byProv[p].gst = Math.round(byProv[p].gst * 100) / 100;
    byProv[p].pst = Math.round(byProv[p].pst * 100) / 100;
  }
  const isUsa = lineItems.length > 0 && lineItems.every(isUsaLine);
  return { rows, totals, byProv, isUsa };
}
